'use client';

import {useEffect, useRef, useState} from 'react';

import type {LinkProps} from '../typings';
import type {MobileNavigationProps as Props} from './typings';

import Icon from '@/atoms/Icon/Icon';
import useFocusTrap from '@/hooks/use-focus-trap/use-focus-trap';
import constantsFactory from '@/utils/constants';
import MobileSubMenuList from './MobileSubMenuList';
import MobileTopNavigation from './MobileTopNavigation';
import SideMenuDrawer from './SideMenuDrawer';
import SideMenuList from './SideMenuList';
import SideMenuUserOptions from './SideMenuUserOptions';
import {
  StyledCloseButton,
  StyledFirstLevelMenu,
  StyledGoBackButton,
  StyledSecondLevelContent,
  StyledSecondLevelMenu,
  StyledSideMenuButton,
  StyledSideMenuControls,
  StyledSideMenuListItem,
} from './styles';

const {DATA_TEST_ID} = constantsFactory();
const slideAnimationDuration = 200;
const wait = (timeout: number) => new Promise((resolve) => setTimeout(resolve, timeout));
export const SIDE_NAV_ARIA = {
  FIRST_LEVEL: {
    ACTIVE: 'Side navigation menu active',
    INERT: 'Side navigation menu inert',
  },
  SECOND_LEVEL: {
    ACTIVE: 'Side navigation submenu active',
    INERT: 'Side navigation submenu inert',
  },
} as const;

/**
 * @description Get the top-level links from the shared navigation
 * @param {Array} linkProps shared navigation links
 * @return {Array} top-level links with their text, component, link, and submenu
 */
export const getTopLevelLinkLabels = (
  linkProps: Props['sharedNavigation'],
): (Pick<LinkProps, 'text' | 'component' | 'link'> & {hasSubMenu: boolean})[] =>
  linkProps.map(({text, component, link, submenu}) => ({
    text,
    component,
    link,
    hasSubMenu: !!submenu,
  }));

/**
 * @description custom hook to track the previous value of a state
 * @param {T} value state value to be tracked
 * @return {T | undefined} previous state value
 */
const usePreviousState = <T = unknown,>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * @description Mobile navigation component, that consists of
 * - top navigation bar
 * - side menu top-level nav inside the drawer
 * - side menu second-level nav inside the drawer
 * @param {object} props props that contain shared navigation tree
 * @return {JSX.Element} Mobile navigation markup
 */
const MobileNavigation = ({sharedNavigation, auth, userSettings}: Props): JSX.Element => {
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const wasPreviouslyOpen = usePreviousState(isSideMenuOpen);
  const [selectedSubMenu, setSelectedSubMenu] = useState<null | LinkProps['submenu']>(null);
  const mountedRef = useRef(true);
  const topLevelRef = useRef<HTMLDivElement>(null);
  const secondLevelRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const topLevelLinkLabels = getTopLevelLinkLabels(sharedNavigation);
  const isSideMenuTopLevelActive = isSideMenuOpen && !selectedSubMenu;
  const isSideMenuSecondLevelActive = isSideMenuOpen && !!selectedSubMenu;
  const isMenuButtonFocused = wasPreviouslyOpen === true && !isSideMenuOpen;

  const handleSelectSubMenu = (topLevelLink: string) => {
    const submenu = sharedNavigation.find(({link}) => link === topLevelLink)?.submenu || null;
    if (mountedRef.current) {
      setSelectedSubMenu(submenu);
    }
  };

  const handleCloseSubMenu = async () => {
    await wait(slideAnimationDuration);
    if (mountedRef.current) {
      setSelectedSubMenu(null);
    }
  };

  const handleCloseSideMenu = () => {
    setIsSideMenuOpen(false);
    setSelectedSubMenu(null);
  };

  const handleCloseOnEscKey = () => {
    if (isSideMenuSecondLevelActive) {
      handleCloseSubMenu();
    } else {
      handleCloseSideMenu();
    }
  };

  useFocusTrap({
    containerRef: isSideMenuTopLevelActive
      ? topLevelRef
      : isSideMenuSecondLevelActive
      ? secondLevelRef
      : null,
    onEscKeydown: handleCloseOnEscKey,
    slideAnimationDuration,
  });

  /**
   * @description restore focus on button when the menu closes
   */
  useEffect(() => {
    if (isMenuButtonFocused) {
      menuButtonRef.current?.focus();
    }
  }, [isMenuButtonFocused]);

  /**
   * @description track the component mount status
   * to prevent set state updates on unmounted component
   */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <>
      <MobileTopNavigation
        auth={auth}
        userSettings={userSettings}
        handleOpenSideMenu={() => {
          setIsSideMenuOpen(true);
        }}
        ref={menuButtonRef}
      />
      <SideMenuDrawer isOpen={isSideMenuOpen} onClose={handleCloseSideMenu}>
        <StyledFirstLevelMenu
          aria-label={
            isSideMenuTopLevelActive
              ? SIDE_NAV_ARIA.FIRST_LEVEL.ACTIVE
              : SIDE_NAV_ARIA.FIRST_LEVEL.INERT
          }
          data-testid={DATA_TEST_ID.SIDE_NAVIGATION}
          ref={topLevelRef}
          tabIndex={-1}
        >
          <StyledSideMenuControls>
            <StyledCloseButton
              tabIndex={isSideMenuTopLevelActive ? 0 : -1}
              onClick={handleCloseSideMenu}
              aria-label="Close navigation"
            >
              <Icon type="plus" fontSize={24} label="Close" />
            </StyledCloseButton>
          </StyledSideMenuControls>
          <SideMenuList aria-label="Side menu top-level list" isTopLevel>
            {topLevelLinkLabels.map(({text, component, link, hasSubMenu}) => (
              <StyledSideMenuListItem
                key={link}
                aria-label={
                  text ? (text && hasSubMenu ? `Opens submenu to ${text}` : text) : undefined
                }
              >
                {hasSubMenu ? (
                  <StyledSideMenuButton
                    tabIndex={isSideMenuTopLevelActive ? 0 : -1}
                    onClick={() => {
                      link && handleSelectSubMenu(link);
                    }}
                  >
                    {text || component}
                  </StyledSideMenuButton>
                ) : (
                  <a
                    tabIndex={isSideMenuTopLevelActive ? 0 : -1}
                    href={link as string}
                    onClick={handleCloseSideMenu}
                  >
                    {text || component}
                  </a>
                )}
              </StyledSideMenuListItem>
            ))}
          </SideMenuList>
          <SideMenuUserOptions
            auth={auth}
            handleCloseSideMenu={handleCloseSideMenu}
            isActive={isSideMenuTopLevelActive}
          />
        </StyledFirstLevelMenu>

        <StyledSecondLevelMenu
          data-testid={DATA_TEST_ID.SIDE_NAVIGATION_SUBMENU}
          aria-label={
            isSideMenuSecondLevelActive
              ? SIDE_NAV_ARIA.SECOND_LEVEL.ACTIVE
              : SIDE_NAV_ARIA.SECOND_LEVEL.INERT
          }
          $isOpen={!!selectedSubMenu}
          $animationDuration={slideAnimationDuration}
          ref={secondLevelRef}
          tabIndex={-1}
        >
          <StyledSideMenuControls $isSubMenu>
            <StyledGoBackButton
              aria-hidden={!isSideMenuSecondLevelActive}
              disabled={!isSideMenuSecondLevelActive}
              onClick={handleCloseSubMenu}
              aria-label="Back to main menu"
              tabIndex={isSideMenuSecondLevelActive ? 0 : -1}
            >
              <Icon type="arrow-left" fontSize={16} label="Back" />
            </StyledGoBackButton>

            <StyledCloseButton
              aria-hidden={!isSideMenuSecondLevelActive}
              disabled={!isSideMenuSecondLevelActive}
              onClick={handleCloseSideMenu}
              aria-label="Close navigation"
              tabIndex={isSideMenuSecondLevelActive ? 0 : -1}
            >
              <Icon type="plus" fontSize={24} label="Close" />
            </StyledCloseButton>
          </StyledSideMenuControls>

          <StyledSecondLevelContent>
            {selectedSubMenu &&
              selectedSubMenu.map((item, index) => {
                return (
                  <MobileSubMenuList
                    key={index}
                    submenu={item}
                    handleItemClick={handleCloseSideMenu}
                    isActive={isSideMenuSecondLevelActive}
                  />
                );
              })}
          </StyledSecondLevelContent>
        </StyledSecondLevelMenu>
      </SideMenuDrawer>
    </>
  );
};

export default MobileNavigation;
